// External imports
import React, { useEffect } from 'react';
import {
  Button,
  AppBar,
  Toolbar,
  Typography,
  NativeSelect,
  FormControl,
  withStyles,
} from '@material-ui/core';
// Internal imports
import {
  ROLE_TYPE,
  HIERARCHY_TYPE,
  EARLIEST_ACTIVE_SEASON,
  CURRENT_SEASON,
  NEXT_SEASON_LICENSE,
} from '~/app/constants.js';
import { useSetState } from '~/app/Utility/customHooks.js';
import { useSelector } from 'react-redux';
import OfficeRadioList from '#/Auth/OfficeRadioList.jsx';
import { getHighLowLicensedSeasons } from '~/app/Utility/general';
// Styling imports
import { styles } from '~app/Components/Auth/css/switchOfficeYear.js';
import '~app/Components/Auth/css/switchOfficeYear.css';

/**
 * Handles displaying the Switch Office or Tax Year modal
 *
 * @Component SwitchOfficeYear
 */
const SwitchOfficeYear = props => {
  const { classes } = props;
  const { currentOfficeID } = useSelector(state => ({
    currentOfficeID: state.app.loggedInBasicInfo.office_id,
  }));
  const [state, setState] = useSetState({
    selectedOfficeID: '',
    season: '',
    licenseInfo: [],
    isOfficeSelected: false,
    byPassLicense: false,
  });

  useEffect(() => {
    if (
      [ROLE_TYPE.EFIN, ROLE_TYPE.PREPARER].includes(
        props.drilldownHistory[props.drilldownHistory?.length - 1].role,
      )
    ) {
      const licenseInfo = [...props.licenseInfo];

      setState({
        licenseInfo,
      });
    } else {
      // If current login is multi-office and greater, bypass license
      // Having issue with MUI disabled property on handling truthy/falsey condition if user is MO and greater
      setState({
        byPassLicense: true,
        licenseInfo: [{}],
      });
    }

    let selectedOfficeID;

    // Grab list osf offices associated to a preparer
    // If for some reason this list of offices comes back as null, attempt to pull the office info via the preparer info
    if (
      (props.payload.hierarchy_type_id === HIERARCHY_TYPE.PREPARER ||
        props.payload.hierarchy_type_id === HIERARCHY_TYPE.EFIN) &&
      props.payload.offices
    ) {
      const currentOffice = props.payload.offices.find(office => office.is_active === true);
      selectedOfficeID = currentOffice?.office_id;
    } else if (
      props.payload.hierarchy_type_id === HIERARCHY_TYPE.PREPARER &&
      props.payload.preparer_info !== null
    ) {
      const currentOffice = props.payload.preparer_info.offices.find(
        office => office.office_id === props.payload.preparer_info.active_office_id,
      );
      selectedOfficeID = currentOffice.office_id;
    }

    setState({
      selectedOfficeID,
    });
    return () => {};
  }, []);

  /**
   * Handles selecting an office
   *
   * @param {Object} e eventDOM used retrieving target value
   */
  const handleOfficeChange = e => {
    setState({
      selectedOfficeID: e,
      isOfficeSelected: !(parseInt(e) === currentOfficeID),
    });
  };

  /**
   * Handles changing tax year
   *
   * @param {string} season season for tax year
   */
  const handleSeasonChange = season => {
    setState({ season });
  };

  /**
   * doSwitch handles a request to switch the current tax year.
   * If list of offices is null for some reason, use the current OfficeID state instead of looking for the selected dropdown value
   */
  const doSwitch = () => {
    if (
      props.payload.hierarchy_type_id === HIERARCHY_TYPE.PREPARER ||
      props.payload.hierarchy_type_id === HIERARCHY_TYPE.EFIN
    ) {
      if (props.payload.offices) {
        const offices = props.payload.offices;
        const selectedOffice = offices.find(
          office => office?.office_id === parseInt(state.selectedOfficeID),
        );
        if (state.isOfficeSelected) {
          props.onClickContinue(selectedOffice?.office_id, props.payload.season);
        } else {
          props.onClickContinue(currentOfficeID, state.season);
        }
      } else {
        props.onClickContinue(state.selectedOfficeID, state.season);
      }
    } else {
      props.onClickContinue(undefined, state.season);
    }
  };

  /**
   * Handles rendering the dropdown options
   *
   * @returns {HTML} tax year options for the dropdown
   */
  const setupTaxYearSelection = () => {
    const taxYearOptions = [];

    const { highestLicensedSeason, lowestLicensedSeason } = getHighLowLicensedSeasons(
      state.licenseInfo,
    );

    let lowestSeason = EARLIEST_ACTIVE_SEASON;
    let highestSeason = CURRENT_SEASON;

    // Generate all supported seasons unless we are at the office level
    // where we should rely on their highest and lowest licensed seasons
    if (
      [ROLE_TYPE.EFIN, ROLE_TYPE.PREPARER].includes(
        props.drilldownHistory[props.drilldownHistory?.length - 1].role,
      )
    ) {
      lowestSeason = lowestLicensedSeason;
      highestSeason = highestLicensedSeason;
    }

    // Determine if user is allowed early access to restricted seasons
    const isAuthorizedUser =
      ENVIRONMENT !== 'production' ||
      ['NAHMIC', 'AMAPRI00', 'CLOTST', 'JAVJIM'].includes(props.payload.account_code) ||
      props.payload.is_super_user;

    for (let season = lowestSeason; season <= highestSeason; season++) {
      /**
       * ===============================
       * STEP 1: PRE-ROLLOVER RESTRICTION
       * ===============================
       * When NEXT_SEASON_LICENSE is ahead of CURRENT_SEASON (e.g., 2027 vs 2026),
       * we completely hide the next season from ALL users.
       *
       * This prevents any access before rollover begins.
       */
      if (season === NEXT_SEASON_LICENSE && NEXT_SEASON_LICENSE !== CURRENT_SEASON) {
        continue;
      }

      /**
       * ===============================
       * STEP 2: POST-ROLLOVER RESTRICTION
       * ===============================
       * Once CURRENT_SEASON updates to match NEXT_SEASON_LICENSE (e.g., both 2027),
       * we allow ONLY authorized users to access that season.
       *
       * Unauthorized users will not see the current season yet.
       */
      if (
        season === CURRENT_SEASON &&
        NEXT_SEASON_LICENSE === CURRENT_SEASON &&
        !isAuthorizedUser
      ) {
        continue;
      }

      /**
       * Only show supported seasons
       */
      if (season >= EARLIEST_ACTIVE_SEASON) {
        taxYearOptions.unshift(
          <option key={season} value={season}>
            {season - 1} Tax Year
          </option>,
        );
      }
    }

    // Default empty option
    taxYearOptions.unshift(<option key={'initial-season'} value={''}></option>);

    return taxYearOptions;
  };

  return (
    <div className="switch-office-year-title-bar">
      <AppBar position="static">
        <Toolbar className={classes.toolbarColor}>
          <Typography className={classes.typographyStyle}>
            {props.officeSwitchModalActive ? 'Switch Office' : 'Switch Tax Year'}
          </Typography>
          <Button className={classes.closeButton} onClick={props.onClose}>
            X
          </Button>
        </Toolbar>
      </AppBar>
      <FormControl className={classes.modalBody}>
        {state.selectedOfficeID && props.payload.offices && props.officeSwitchModalActive ? (
          <div>
            <span className={classes.headerLabels}>Selected Office</span>
            <OfficeRadioList onOfficeChange={e => handleOfficeChange(e)} />
            <hr />
          </div>
        ) : null}
        {props.taxyearSwitchModalActive && (
          <>
            <span className={classes.headerLabels}>Selected Year</span>
            <NativeSelect
              value={state.season}
              onChange={e => handleSeasonChange(e.target.value)}
              name="season"
              className={classes.ddl}
              disableUnderline={true}
              disabled={!state.licenseInfo.length}
            >
              {state.byPassLicense || state.licenseInfo.length ? (
                setupTaxYearSelection()
              ) : (
                <option value="none">
                  This Office has not yet been assigned a License. Contact your supervisor.
                </option>
              )}
            </NativeSelect>
          </>
        )}
        <div className={`container ${classes.buttonContainer}`}>
          <Button
            id="btnCancelSwtichAccount"
            size="small"
            color="primary"
            className={classes.cancelButton}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            id="btnDoSwtichAccount"
            size="small"
            color="primary"
            variant="contained"
            onClick={doSwitch}
            disabled={
              (state.season === '' && !state.isOfficeSelected) ||
              (!props.officeSwitchModalActive ? !state.licenseInfo.length : false)
            }
          >
            Switch
          </Button>
        </div>
      </FormControl>
    </div>
  );
};

export default withStyles(styles)(SwitchOfficeYear);
